import dagre from 'dagre'
import type { DataNode, DataEdge } from '@/types'

export interface LayoutOptions {
  direction: 'TB' | 'BT' | 'LR' | 'RL'
  nodeWidth: number
  nodeHeight: number
  rankSep: number
  nodeSep: number
  edgeSep: number
  marginX: number
  marginY: number
}

const defaultOptions: LayoutOptions = {
  direction: 'LR',
  nodeWidth: 200,
  nodeHeight: 50,
  rankSep: 150,
  nodeSep: 40,
  edgeSep: 25,
  marginX: 50,
  marginY: 50,
}

export function calculateLayout(
  nodes: DataNode[],
  edges: DataEdge[],
  options: Partial<LayoutOptions> = {}
): { nodes: DataNode[]; edges: DataEdge[] } {
  const opts = { ...defaultOptions, ...options }
  
  const dagreGraph = new dagre.graphlib.Graph()
  dagreGraph.setDefaultEdgeLabel(() => ({}))
  
  dagreGraph.setGraph({
    rankdir: opts.direction,
    ranksep: opts.rankSep,
    nodesep: opts.nodeSep,
    edgesep: opts.edgeSep,
    marginx: opts.marginX,
    marginy: opts.marginY,
  })

  // Add nodes
  nodes.forEach((node) => {
    // Adjust node size based on content
    const labelLength = node.data.label.length
    const width = Math.max(opts.nodeWidth, labelLength * 8 + 60)
    
    dagreGraph.setNode(node.id, { 
      width, 
      height: opts.nodeHeight 
    })
  })

  // Add edges
  edges.forEach((edge) => {
    dagreGraph.setEdge(edge.source, edge.target)
  })

  // Calculate layout
  dagre.layout(dagreGraph)

  // Apply positions to nodes
  const layoutedNodes = nodes.map((node) => {
    const nodeWithPosition = dagreGraph.node(node.id)
    
    return {
      ...node,
      position: {
        x: nodeWithPosition.x - opts.nodeWidth / 2,
        y: nodeWithPosition.y - opts.nodeHeight / 2,
      },
    }
  })

  return {
    nodes: layoutedNodes,
    edges,
  }
}

export function fitViewPadding(nodeCount: number): number {
  if (nodeCount < 10) return 0.2
  if (nodeCount < 50) return 0.1
  if (nodeCount < 100) return 0.05
  return 0.02
}
